// display.status-chip.v1
//
// A pill-style status indicator (info / success / warning / error /
// neutral). Used inline alongside titles to show state. Different from
// a Tag — chips imply a state, tags imply a category.

import { type JSX, Show, splitProps } from "solid-js"
import { Icon, type IconProps } from "../../components/icon"
import type { NoticeIntent } from "../types"

export type StatusChipIntent = NoticeIntent | "neutral"

export interface StatusChipProps {
  label: string
  intent?: StatusChipIntent
  icon?: IconProps["name"]
  class?: string
}

const toneClass = {
  info: "bg-surface-info-weak text-icon-info-base",
  success: "bg-surface-success-weak text-icon-success-base",
  warning: "bg-surface-warning-weak text-icon-warning-base",
  error: "bg-surface-critical-weak text-icon-critical-base",
  neutral: "bg-surface-raised-base text-text-base",
} as const satisfies Record<StatusChipIntent, string>

export function StatusChip(props: StatusChipProps): JSX.Element {
  const [local, rest] = splitProps(props, ["label", "intent", "icon", "class"])
  const intent = local.intent ?? "neutral"

  return (
    <span
      data-registry-id="display.status-chip"
      data-intent={intent}
      classList={{
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-12-medium": true,
        [toneClass[intent]]: true,
        [local.class ?? ""]: !!local.class,
      }}
      {...rest}
    >
      <Show when={local.icon}>
        {(name) => <Icon name={name()} size="small" />}
      </Show>
      <span class="truncate">{local.label}</span>
    </span>
  )
}
